import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Divider,
  CircularProgress
} from '@mui/material';
import { Save as SaveIcon, History as HistoryIcon, Warning as WarningIcon } from '@mui/icons-material';
import dataRetentionService, { DataRetentionPolicy } from '../api/services/dataRetention';

const DataRetention: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Map categories to policy IDs if they exist
  const [policyMap, setPolicyMap] = useState<{ [key: string]: string }>({});

  const [retentionPolicy, setPolicy] = useState({
    invoices: 7, // Years (UK standard is 6 years + current year)
    transactions: 7,
    employeeRecords: 3,
    auditLogs: 1,
    autoArchive: true
  });

  const categories = {
    invoices: 'financial_records',
    transactions: 'bank_transactions',
    employeeRecords: 'employee_records',
    auditLogs: 'system_logs'
  };

  const loadPolicies = async () => {
    try {
      setLoading(true);
      const { policies } = await dataRetentionService.getPolicies();

      const newPolicyMap: { [key: string]: string } = {};
      const newValues = { ...retentionPolicy };

      policies.forEach(p => {
        if (p.data_category === categories.invoices) {
          newValues.invoices = Math.round(p.retention_period_days / 365);
          newPolicyMap.invoices = String(p.id);
        } else if (p.data_category === categories.transactions) {
          newValues.transactions = Math.round(p.retention_period_days / 365);
          newPolicyMap.transactions = String(p.id);
        } else if (p.data_category === categories.employeeRecords) {
          newValues.employeeRecords = Math.round(p.retention_period_days / 365);
          newPolicyMap.employeeRecords = String(p.id);
        } else if (p.data_category === categories.auditLogs) {
          newValues.auditLogs = Math.round(p.retention_period_days / 365);
          newPolicyMap.auditLogs = String(p.id);
        }

        // Assume global auto archive setting from one of them or use a separate setting
        // For now, we'll just check if any have auto archive enabled
        if (p.auto_archive_enabled) {
          newValues.autoArchive = true;
        }
      });

      setPolicy(newValues);
      setPolicyMap(newPolicyMap);
    } catch (err: any) {
      console.error("Failed to load policies", err);
      // Don't show error to user immediately, just fallback to defaults
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPolicies();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const updates = [
        { key: 'invoices', days: retentionPolicy.invoices * 365, category: categories.invoices },
        { key: 'transactions', days: retentionPolicy.transactions * 365, category: categories.transactions },
        { key: 'employeeRecords', days: retentionPolicy.employeeRecords * 365, category: categories.employeeRecords },
        { key: 'auditLogs', days: retentionPolicy.auditLogs * 365, category: categories.auditLogs },
      ];

      for (const item of updates) {
        const policyId = policyMap[item.key];
        const payload = {
          policy_name: `${item.key} Retention Policy`,
          data_category: item.category,
          retention_period_days: item.days,
          auto_archive_enabled: retentionPolicy.autoArchive,
          enabled: true
        };

        if (policyId) {
          await dataRetentionService.updatePolicy(policyId, payload);
        } else {
          // Create new
          const res = await dataRetentionService.createPolicy({
            ...payload,
            policy_id: item.category, // simplified ID
          });
          // Update map
          setPolicyMap(prev => ({ ...prev, [item.key]: String(res.policy.id) }));
        }
      }

      setSuccess("Retention policies updated successfully.");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || "Failed to save policies.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Data Retention Policy
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        Configure how long your organization retains financial and operational data to comply with regulations like GDPR and IRS requirements.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

      <Alert severity="info" icon={<HistoryIcon />} sx={{ mb: 3 }}>
        <strong>IRS Requirement:</strong> You must keep records for at least 6 years from the end of the last company financial year they relate to.
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Retention Periods (Years)</Typography>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Financial Records (Invoices/Bills)</InputLabel>
                    <Select
                      value={retentionPolicy.invoices}
                      label="Financial Records (Invoices/Bills)"
                      onChange={(e) => setPolicy({ ...retentionPolicy, invoices: Number(e.target.value) })}
                    >
                      <MenuItem value={6}>6 Years (Minimum)</MenuItem>
                      <MenuItem value={7}>7 Years (Recommended)</MenuItem>
                      <MenuItem value={10}>10 Years</MenuItem>
                      <MenuItem value={99}>Indefinite</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Bank Transactions</InputLabel>
                    <Select
                      value={retentionPolicy.transactions}
                      label="Bank Transactions"
                      onChange={(e) => setPolicy({ ...retentionPolicy, transactions: Number(e.target.value) })}
                    >
                      <MenuItem value={6}>6 Years (Minimum)</MenuItem>
                      <MenuItem value={7}>7 Years (Recommended)</MenuItem>
                      <MenuItem value={10}>10 Years</MenuItem>
                      <MenuItem value={99}>Indefinite</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Employee Records (Post-Termination)</InputLabel>
                    <Select
                      value={retentionPolicy.employeeRecords}
                      label="Employee Records (Post-Termination)"
                      onChange={(e) => setPolicy({ ...retentionPolicy, employeeRecords: Number(e.target.value) })}
                    >
                      <MenuItem value={1}>1 Year</MenuItem>
                      <MenuItem value={3}>3 Years</MenuItem>
                      <MenuItem value={6}>6 Years</MenuItem>
                      <MenuItem value={99}>Indefinite</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>System Audit Logs</InputLabel>
                    <Select
                      value={retentionPolicy.auditLogs}
                      label="System Audit Logs"
                      onChange={(e) => setPolicy({ ...retentionPolicy, auditLogs: Number(e.target.value) })}
                    >
                      <MenuItem value={1}>1 Year</MenuItem>
                      <MenuItem value={2}>2 Years</MenuItem>
                      <MenuItem value={5}>5 Years</MenuItem>
                      <MenuItem value={99}>Indefinite</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box sx={{ mt: 4 }}>
                <FormControlLabel
                  control={<Switch checked={retentionPolicy.autoArchive} onChange={(e) => setPolicy({ ...retentionPolicy, autoArchive: e.target.checked })} />}
                  label="Automatically archive data past retention period (Data is moved to cold storage, not deleted)"
                />
              </Box>

            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="warning" /> GDPR Warning
              </Typography>
              <Typography variant="body2" paragraph>
                Personal data should not be kept longer than necessary. Ensure your "Employee Records" retention aligns with your privacy policy.
              </Typography>
              <Button
                variant="outlined"
                color="warning"
                fullWidth
                onClick={() => navigate('/privacy-policy')}
              >
                View Privacy Policy
              </Button>
            </CardContent>
          </Card>

          <Button
            variant="contained"
            size="large"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            fullWidth
            sx={{ mt: 3 }}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DataRetention;
